import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { BRAND } from "../lib/config";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import ReviewTipCTA from "../components/ReviewTipCTA";
import { toast } from "sonner";
import { InstallAppButton } from "../components/PWAInstallBanner";
import { usePushNotifications } from "../lib/pwa";
import {
  Phone, MessageSquare, Calendar, Repeat, Image as ImageIcon, Info, LogOut, ChevronRight, Copy, Loader2, Share2, Bell, BellOff, ShieldCheck, CreditCard, Trash2, Mail, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/config";

function Row({ to, href, onClick, icon: Icon, label, danger, testid }) {
  const inner = (
    <div className="flex items-center gap-4 py-4 px-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${danger ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-[#047857]"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className={`flex-1 font-bold text-[15px] ${danger ? "text-rose-600" : "text-gray-900"} font-[Outfit]`}>{label}</div>
      <ChevronRight className={`w-5 h-5 ${danger ? "text-rose-300" : "text-gray-300"}`} />
    </div>
  );
  if (to) return <Link to={to} data-testid={testid} className="block hover:bg-gray-50 active:bg-gray-100 transition-colors">{inner}</Link>;
  if (href) return <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noopener noreferrer" : undefined} data-testid={testid} className="block hover:bg-gray-50 active:bg-gray-100 transition-colors">{inner}</a>;
  return <button type="button" onClick={onClick} data-testid={testid} className="w-full text-left hover:bg-gray-50 active:bg-gray-100 transition-colors">{inner}</button>;
}

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  }

  const code = user.referral_code || "JDSN-XXXXXX";
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Referral code copied!");
    } catch {
      toast.error("Could not copy");
    }
  };
  const shareReferral = async () => {
    const text = `Use my JDSN Lawn Care code ${code} — you'll get 15% off your first service! Book at ${window.location.origin}`;
    if (navigator.share) {
      try { await navigator.share({ title: "JDSN Lawn Care", text, url: window.location.origin }); } catch { /* noop */ }
    } else {
      copyCode();
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="profile-page">
      {/* JDSN brand mark */}
      <div className="flex justify-center mb-6">
        <Logo size={56} />
      </div>

      {/* Avatar + name */}
      <div className="flex flex-col items-center text-center">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-24 h-24 rounded-full border-4 border-white shadow-lg shadow-black/5 object-cover" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg shadow-emerald-600/10 flex items-center justify-center overflow-hidden">
            <img src="/icons/jdsn-logo.png" alt="" className="w-20 h-20 object-contain" />
          </div>
        )}
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-gray-900 font-[Outfit]" data-testid="profile-name">{user.name || "Friend"}</h1>
        <p className="text-sm text-gray-500">{user.email}</p>
      </div>

      {/* Call / Text */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <a href={`tel:${BRAND.phoneTel}`} data-testid="profile-call-btn" className="h-14 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white font-bold flex items-center justify-center gap-2 shadow-md shadow-emerald-600/25 active:scale-[0.98] transition-transform">
          <Phone className="w-5 h-5" /> Call {BRAND.phone}
        </a>
        <a href={`sms:${BRAND.phoneTel}`} data-testid="profile-text-btn" className="h-14 rounded-full bg-[#0A2E1E] text-white font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <MessageSquare className="w-5 h-5" /> Text Us
        </a>
      </div>

      {/* Referral code */}
      <div className="mt-6 rounded-3xl bg-gradient-to-br from-[#0A2E1E] via-[#093924] to-[#062818] text-white p-6 relative overflow-hidden" data-testid="referral-card">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-400/15 blur-2xl" />
        <div className="absolute -bottom-12 -left-12 w-32 h-32 rounded-full bg-emerald-300/10 blur-2xl" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Your Referral Code</div>
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-3xl sm:text-4xl font-extrabold tracking-tight font-[Outfit]" data-testid="referral-code">{code}</div>
            <div className="flex gap-2">
              <button onClick={copyCode} data-testid="copy-code-btn" className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" aria-label="Copy"><Copy className="w-4 h-4" /></button>
              <button onClick={shareReferral} data-testid="share-code-btn" className="h-10 px-4 rounded-full bg-white text-[#062818] font-bold flex items-center gap-1.5 hover:bg-emerald-50 transition-colors text-sm"><Share2 className="w-4 h-4" /> Share</button>
            </div>
          </div>
          <p className="mt-3 text-sm text-emerald-100/80 leading-relaxed">Share with friends — they get <span className="font-bold text-white">15% off</span>, you save on your next visit.</p>
        </div>
      </div>

      {/* Main menu */}
      <div className="mt-6 rounded-3xl bg-white border border-gray-200 overflow-hidden divide-y divide-gray-100">
        <Row to="/dashboard" icon={Calendar} label="My bookings" testid="profile-row-bookings" />
        <Row to="/book?recurring=1" icon={Repeat} label="Recurring plans" testid="profile-row-recurring" />
        <Row href={`sms:${BRAND.phoneTel}`} icon={MessageSquare} label="Live chat & SMS" testid="profile-row-chat" />
        <Row to="/gallery" icon={ImageIcon} label="Before & After gallery" testid="profile-row-gallery" />
      </div>

      {/* Payment & AutoPay (iter175) — dedicated Payment Methods page */}
      <div className="mt-4">
        <PaymentMethodsSummaryRow />
      </div>

      {/* iter221 — Automatic Tip toggle. Read-only ON/OFF summary card
          the customer can flip anytime. Same schema powers the tip
          page's opt-in checkbox. */}
      <div className="mt-4">
        <AutomaticTipCard />
      </div>

      {/* Google Review + Tip + Book Next — same design used in emails & dashboard */}
      <div className="mt-4">
        <ReviewTipCTA />
      </div>

      {/* App & notifications */}
      <div className="mt-4 rounded-3xl bg-white border border-gray-200 overflow-hidden divide-y divide-gray-100">
        <InstallAppButton variant="row" />
        <NotificationsRow />
        <SmsOptInRow />
        <MarketingOptInRow />
        <SurchargeTermsRow />
      </div>

      <div className="mt-4 rounded-3xl bg-white border border-gray-200 overflow-hidden divide-y divide-gray-100">
        <Row onClick={() => setAboutOpen(!aboutOpen)} icon={Info} label="About JDSN" testid="profile-row-about" />
        <Row onClick={logout} icon={LogOut} label="Sign out" danger testid="profile-row-signout" />
      </div>

      {aboutOpen && (
        <div className="mt-3 rounded-2xl bg-white border border-gray-200 p-5 text-sm text-gray-700 leading-relaxed" data-testid="about-content">
          <p><span className="font-bold text-gray-900">JDSN Lawn Care</span> — premium lawn, landscape and yard services across Jacksonville, FL. Family owned. Licensed & insured.</p>
          <p className="mt-2">Same-week scheduling. Honest dynamic pricing. Photos before & after every job.</p>
        </div>
      )}

      <div className="mt-8 text-center text-xs text-gray-400">
        <div>JDSN Lawn Care · Jacksonville, FL</div>
        <div className="mt-0.5">{BRAND.phone}</div>
      </div>
    </div>
  );
}

function SmsOptInRow() {
  // iter164 — Customer-facing toggle for transactional SMS reminders.
  // Default is opted-in on signup. Backed by /api/me/sms-opt-in.
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/sms-opt-in");
        setEnabled(!!data.sms_opt_in);
      } catch { /* silent */ }
      finally { setLoaded(true); }
    })();
  }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      const next = !enabled;
      await api.put("/me/sms-opt-in", { sms_opt_in: next });
      setEnabled(next);
      toast.success(next ? "SMS reminders on" : "SMS reminders off");
    } catch { toast.error("Could not update SMS preference"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy || !loaded}
            data-testid="profile-row-sms-optin"
            className="w-full text-left hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-full bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
          <MessageSquare className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold text-gray-900">SMS reminders</div>
          <div className="text-[11px] text-gray-500">{enabled ? "You'll get a text 24 hours before each visit." : "Text reminders are off. You'll still get email reminders."}</div>
        </div>
        <div className={`w-10 h-6 rounded-full relative transition ${enabled ? "bg-[#047857]" : "bg-gray-300"}`}
             data-testid="profile-sms-optin-toggle" data-state={enabled ? "on" : "off"}>
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${enabled ? "left-[18px]" : "left-0.5"}`} />
        </div>
      </div>
    </button>
  );
}


function MarketingOptInRow() {
  // opt-in. Backed by /me/settings/marketing-opt-in; drives the weekly
  // marketing campaign (Slice B) so the customer sees a real effect.
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const optedIn = !!user?.email_marketing_opt_in;
  const toggle = async () => {
    setBusy(true);
    try {
      const next = !optedIn;
      const { data } = await api.post("/me/settings/marketing-opt-in", { email_marketing_opt_in: next });
      if (setUser && user) setUser({ ...user, email_marketing_opt_in: !!data.email_marketing_opt_in });
      toast.success(next ? "Subscribed to promotional emails" : "Unsubscribed from promotional emails");
    } catch { toast.error("Could not update preferences"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy}
            data-testid="profile-row-marketing-optin"
            className="w-full text-left hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-full bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
          <Mail className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold text-gray-900">Promotional emails</div>
          <div className="text-[11px] text-gray-500">{optedIn ? "You're subscribed — one email per week, tops. Unsubscribe anytime." : "You're not receiving promotional emails."}</div>
        </div>
        <div className={`w-10 h-6 rounded-full relative transition ${optedIn ? "bg-[#047857]" : "bg-gray-300"}`}
             data-testid="profile-marketing-optin-toggle" data-state={optedIn ? "on" : "off"}>
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${optedIn ? "left-[18px]" : "left-0.5"}`} />
        </div>
      </div>
    </button>
  );
}


function NotificationsRow() {
  const { supported, subscribed, busy, subscribe, unsubscribe, permission } = usePushNotifications();
  if (!supported) return null;
  const onClick = async () => {
    if (subscribed) {
      const r = await unsubscribe();
      if (r.ok) toast.success("Notifications turned off");
    } else {
      const r = await subscribe();
      if (r.ok) toast.success("Notifications enabled");
      else if (r.reason === "denied") toast.error("Notifications blocked in browser settings");
      else if (r.reason !== "unsupported") toast.error("Could not enable notifications");
    }
  };
  const blocked = permission === "denied";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || blocked}
      data-testid="profile-row-notifications"
      className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 text-left disabled:opacity-60"
    >
      <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${subscribed ? "bg-emerald-50 text-[#047857]" : "bg-gray-100 text-gray-500"}`}>
        {subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-extrabold tracking-tight text-gray-900">
          {subscribed ? "Notifications ON" : blocked ? "Notifications blocked" : "Turn on notifications"}
        </span>
        <span className="block text-[12px] text-gray-500">
          {blocked ? "Enable in browser settings to receive alerts" : "Quote approvals · On the way · Booking & payment updates"}
        </span>
      </span>
      {busy ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
    </button>
  );
}

function SurchargeTermsRow() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const accepted = !!user?.surcharge_terms_accepted_at;

  const toggle = async () => {
    setBusy(true);
    try {
      if (accepted) {
        await api.post("/user/revoke-surcharge-terms");
        toast.success("Auto-charge authorization revoked");
      } else {
        await api.post("/user/accept-surcharge-terms");
        toast.success("Auto-charge authorized · 5h grace applies");
      }
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid="profile-row-surcharge-terms"
      className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 text-left disabled:opacity-60"
    >
      <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${accepted ? "bg-emerald-50 text-[#047857]" : "bg-gray-100 text-gray-500"}`}>
        <ShieldCheck className="w-5 h-5" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-extrabold tracking-tight text-gray-900">
          {accepted ? "Auto-charge authorized" : "Authorize surcharge auto-charge"}
        </span>
        <span className="block text-[12px] text-gray-500">
          {accepted
            ? "Approved-service surcharges will auto-charge your card if you don't respond within 5 hours."
            : "Allow overgrown-grass & approved-service surcharges to auto-charge your card on file after a 5-hour grace period."}
        </span>
      </span>
      {busy ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
    </button>
  );
}


// iter175 — Compact summary row on the profile page. Displays the current
// card + AutoPay status inline and links to the dedicated Payment Methods
// page (/profile/payment-methods) for full management (Update, Remove,
// audit log). The prior in-place remove flow has been retired in favour
// of the dedicated page with a proper Shadcn confirmation dialog.
function PaymentMethodsSummaryRow() {
  const [state, setState] = useState({ loading: true, has_card: false });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/saved-card");
        setState({ loading: false, ...data });
      } catch {
        setState({ loading: false, has_card: false });
      }
    })();
  }, []);

  if (state.loading) {
    return (
      <div className="rounded-3xl bg-white border border-gray-200 p-6 flex items-center justify-center" data-testid="saved-card-section">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  const hasCard = !!state.has_card;
  const autopayActive = !!state.autopay_enabled && !state.autopay_admin_disabled && hasCard;
  const brand = (state.brand || "").toString();
  const brandCap = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
  const last4 = state.last4 || "••••";
  const exp = state.exp_month && state.exp_year
      ? `${String(state.exp_month).padStart(2, "0")}/${String(state.exp_year).slice(-2)}`
      : null;
  const pmRequired = !!state.payment_method_required;

  return (
    <Link
      to="/profile/payment-methods"
      className="block rounded-3xl bg-white border border-gray-200 overflow-hidden hover:border-emerald-300 hover:shadow-sm transition"
      data-testid="saved-card-section"
    >
      <div className="p-5 flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${hasCard ? "bg-[#062818] text-white" : "bg-emerald-100 text-[#047857]"}`}>
          <CreditCard className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
            Payment methods
          </div>
          {hasCard ? (
            <div className="text-[15px] font-extrabold text-gray-900 leading-tight" data-testid="saved-card-summary">
              {brandCap} •••• {last4}
              {exp && <span className="ml-2 text-xs font-medium text-gray-500">exp {exp}</span>}
            </div>
          ) : (
            <div className="text-[15px] font-extrabold text-gray-900 leading-tight">
              Add a payment method
            </div>
          )}
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {hasCard && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-extrabold ${autopayActive ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                data-testid="saved-card-autopay-status"
              >
                AutoPay {autopayActive ? "on" : "paused"}
              </span>
            )}
            {pmRequired && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-extrabold bg-amber-100 text-amber-900">
                <AlertTriangle className="w-3 h-3" /> Action needed
              </span>
            )}
            {!hasCard && !pmRequired && (
              <span className="text-xs text-gray-500">Enable AutoPay for recurring visits</span>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </Link>
  );
}


// iter221 — Automatic Tip card. Shows the current ON/OFF state,
// authorized amount, and lets the customer disable it in one tap.
// Re-enabling is intentionally NOT possible from here without an
// explicit authorization — the customer must authorize again from the
// /tip/:booking_id page after a completed service (owner rule:
// authorization always requires a fresh consent moment).
function AutomaticTipCard() {
  const [state, setState] = useState({ enabled: false, amount: 0, updated_at: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/tip-preference");
      setState({
        enabled: !!data.enabled,
        amount: Number(data.amount || 0),
        updated_at: data.updated_at || null,
        source: data.source || null,
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const disable = async () => {
    setBusy(true);
    try {
      await api.put("/me/tip-preference", { enabled: false, amount: state.amount || 0 });
      setState((s) => ({ ...s, enabled: false }));
      toast.success("Automatic tip turned off.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update preference");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="rounded-3xl bg-white border border-gray-200 p-4 flex items-center gap-3"
            data-testid="automatic-tip-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">Loading tip preferences…</span>
      </div>
    );
  }

  if (!state.enabled) {
    return (
      <div className="rounded-3xl bg-white border border-gray-200 p-5"
            data-testid="automatic-tip-card-off">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Repeat className="w-5 h-5 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-gray-900 font-[Outfit]">Automatic Tip</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Currently off. You can turn it on from the tip page after any
              completed service — service + tip is charged as one total.
            </div>
          </div>
          <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border border-gray-200 bg-gray-50 text-gray-500 px-2 py-0.5"
                data-testid="automatic-tip-status-badge">
            OFF
          </span>
        </div>
      </div>
    );
  }

  const when = (() => {
    try {
      if (!state.updated_at) return null;
      return new Date(state.updated_at).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return null; }
  })();

  return (
    <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 p-5"
          data-testid="automatic-tip-card-on">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Repeat className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-gray-900 font-[Outfit]">
            Automatic Tip is <span className="text-emerald-700">ON</span>
          </div>
          <div className="text-xs text-gray-600 mt-0.5"
                data-testid="automatic-tip-details">
            <span className="font-bold text-emerald-800">
              ${state.amount.toFixed(2)}
            </span>{" "}
            added to every completed service.
            {when && (<> Authorized {when}.</>)}
          </div>
        </div>
        <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border border-emerald-300 bg-emerald-100 text-emerald-800 px-2 py-0.5"
              data-testid="automatic-tip-status-badge">
          ON
        </span>
      </div>
      <Button
        onClick={disable}
        disabled={busy}
        className="mt-4 w-full h-11 rounded-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 font-extrabold text-sm"
        data-testid="automatic-tip-disable-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Turn automatic tip OFF"}
      </Button>
    </div>
  );
}
